<script lang="ts">
    import { createEventDispatcher } from "svelte";

    export let options: (string|number|{label: string, selectedLabel?: string, value: string|number|null})[] = [];
    export let value: string|number|null = "";
    export let enterValue: boolean = true;
    export let step: number|null = null;
    export let percentStep: number|null = null;
    export let min: number|null = null;
    export let max: number|null = null;
    export let filter: boolean = false;
    export let stepMultiple: boolean = true;
    export let inputWidth: number|null = null;
    export let showArrow: boolean = true;
    export let showBorder: boolean = false;
    export let parseValue: (val: string|number|null) => string|number|null = (val: string|number|null) => val;

    const dispatch = createEventDispatcher();

    let inputValue: string = valueToString(value);
    let filtered: (string|number|{label: string, value: string|number|null})[] = [];
    let dropdownVisible: boolean = false;
    let activeIndex: number = -1;

    let inputElement: any;
    let dropdownElement: any;

    window.addEventListener('scroll', repositionDropdown, true);
    window.addEventListener('resize', repositionDropdown);

    // Sync external value
    $: inputValue = valueToString(value);

    // Filter options
    $: {
        if (filter) {
            filtered = options.filter(o => {
                let val = typeof(o) == 'number' ? o.toString() : (typeof(o) == 'string' ? o : o.value.toString())
                val.toLowerCase().includes(inputValue.toString().toLowerCase())
            });
        } else {
            filtered = options;
        }
    }

    function valueToString(value: string|number|null) {
        let matchingOption = options.filter(o => o.value === value)[0] || null;
        if (matchingOption) {
            return matchingOption.selectedLabel || matchingOption.label;
        } else if (value === null || (typeof(value) == 'number' && isNaN(value))) {
            return '';
        } else {
            return value.toString();
        }
    }

    function update(val: string|number) {
        if (min !== null || max !== null) {
            let num = typeof(val) == 'string' ? parseFloat(val) : val;
            if (!isNaN(num)) {
                if (min !== null) num = Math.max(min, num);
                if (max !== null) num = Math.min(max, num);
                val = typeof(val) == 'string' ? num.toString() : num;
            }
        }
        value = parseValue(typeof(value) == 'number' && typeof(val) == 'string' ? parseFloat(val) : (typeof(value) == 'string' && typeof(val) == 'number' ? val.toString() : val));
        dispatch("change", value);
    }

    function repositionDropdown() {
        if (dropdownVisible) {
            let inputRect = inputElement.getBoundingClientRect();
            dropdownElement.style.opacity = 0;
            dropdownElement.style.minWidth = (inputRect.width)+'px';
            dropdownElement.style.display = 'block';
            dropdownElement.style.maxHeight = '';
            document.body.appendChild(dropdownElement);
            let dropdownRect = dropdownElement.getBoundingClientRect();
            if (inputRect.top + inputRect.height + dropdownRect.height <= window.innerHeight) {
                dropdownElement.style.top = (inputRect.top + inputRect.height)+'px';
            } else if (inputRect.top >= dropdownRect.height) {
                dropdownElement.style.top = (inputRect.top - dropdownRect.height)+'px';
            } else if (window.innerHeight - inputRect.top - inputRect.height > inputRect.top) {
                dropdownElement.style.top = (inputRect.top + inputRect.height)+'px';
                dropdownElement.style.maxHeight = (window.innerHeight - inputRect.top - inputRect.height)+'px';
            } else {
                dropdownElement.style.top = (inputRect.top - dropdownRect.height)+'px';
                dropdownElement.style.maxHeight = (inputRect.top)+'px';
            }
            dropdownElement.style.left = (inputRect.left)+'px';
            dropdownElement.style.opacity = 1;
        }
    }

    function showDropdown() {
        dropdownVisible = true;
        repositionDropdown();
    }

    function hideDropdown() {
        dropdownElement.style.display = 'none';
        dropdownElement.remove();
        dropdownVisible = false;
    }

    function handleInput(e: Event) {
        inputValue = e.target.value;
        showDropdown();
        activeIndex = -1;
    }

    function selectOption(opt: (string|number|{label: string, selectedLabel?: string, value: string|number|null})) {
        inputValue = typeof(opt) == 'object' ? (opt.selectedLabel || opt.label) : opt.toString();
        update(typeof(opt) == 'object' ? opt.value : opt);
        hideDropdown();
    }

    function increment(dir = 1) {
        let num = typeof(value) == 'string' ? parseFloat(value) : value;
        if (isNaN(num)) return;

        if (percentStep) {
            num += num * (percentStep/100) * dir;
        } else if (step) {
            num += step * dir;
            if (stepMultiple) {
                num = Math.round(num / step) * step;
            }
        }

        if (min !== null) num = Math.max(min, num);
        if (max !== null) num = Math.min(max, num);

        update(num);
    }

    function handleKey(e) {
        if (!dropdownVisible && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            showDropdown();
            return;
        }

        if (e.key === "ArrowDown") {
            activeIndex = (activeIndex + 1) % filtered.length;
            e.preventDefault();
        }

        if (e.key === "ArrowUp") {
            activeIndex =
                (activeIndex - 1 + filtered.length) % filtered.length;
            e.preventDefault();
        }

        if (e.key === "Enter") {
            if (activeIndex >= 0) {
                selectOption(filtered[activeIndex]);
            }
            hideDropdown();
        }

        if (e.key === "Escape") {
            hideDropdown();
        }
    }

    function handleBlur() {
        if (enterValue) {
            let matchingOption = options.filter(o => (o.selectedLabel || o.label) === inputValue)[0] || null;
            update(matchingOption ? matchingOption.value : inputValue);
        }
        setTimeout(() => (hideDropdown()), 100);
    }
</script>

<div class="wrapper">
  <div class="control" class:with-arrow={showArrow} class:with-border={showBorder}>
    {#if step !== null || percentStep !== null}
        <button on:click={() => increment(-1)}>-</button>
    {/if}

    <div class="input-container">
        <input
            bind:this={inputElement}
            bind:value={inputValue}
            on:input={handleInput}
            on:focus={() => { showDropdown(); }}
            on:keydown={handleKey}
            on:blur={handleBlur}
            class:input-set-width={inputWidth !== null}
            class:input-readonly={!enterValue}
            style={(inputWidth ? 'width: '+inputWidth+'px' : '')}
            readonly={!enterValue}
        />
    </div>

    {#if step !== null || percentStep !== null}
        <button on:click={() => increment(1)}>+</button>
    {/if}
  </div>

  {#if filtered.length}
    <div class="dropdown" style="display: none" bind:this={dropdownElement}>
      {#each filtered as opt, i}
        <div
          class="item {i === activeIndex ? 'active' : ''}"
          on:mousedown={() => selectOption(opt)}
        >
          {(typeof(opt) == 'object' ? opt.label : opt)}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .wrapper {
    position: relative;
  }

  .control {
    display: flex;
    border: var(--input-small-border);
    border-radius: var(--input-small-border-radius);
    background-color: #ffffff;
  }

  .control.with-arrow {
    position: relative;
  }

  input {
    flex: 1;
    outline: 0;
    padding: var(--input-small-padding);
    color: var(--input-small-color);
    border: 0px;
    border-radius: var(--input-small-border-radius);
  }

  .control.with-border input {
    border: 1px solid #8f8f8f;
    border-radius: var(--input-small-border-radius);
  }

  input.input-set-width {
    flex: 1
  }

  input.input-readonly {
    cursor: default;
  }

  .control.with-arrow input {
    padding-right: calc(var(--input-small-padding) + 11px);
    box-sizing: content-box;
  }
  .control.with-arrow .input-container {
    position: relative;
  }
  .control.with-arrow .input-container::after {
    content: '';
    position: absolute;
    right: 6px;
    top: 50%;
    margin-top: -4px;
    width: 4px;
    height: 4px;
    border-width: 0 0 1px 1px;
    border-style: solid;
    border-color: var(--input-small-color);
    transform: rotate(-45deg);
    pointer-events: none;
  }

  button {
    width: 32px;
    cursor: pointer;
  }

  .dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 100%;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
    max-height: 150px;
    overflow-y: auto;
    z-index: 1002;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    padding: 5px;
  }

  .item {
    padding: 6px;
    border-radius: 3px;
    cursor: pointer;
    color: var(--input-small-color);
    white-space: nowrap;
  }

  .item.active {
    background: #eee;
  }

  .item:hover {
    background: #ddd;
  }
</style>